import React from 'react';

/**
 * Патронус рисуется как SVG из слоёв: ночная сцена, свечение, тело, поверх —
 * надетые вещи. Цвет не требует новых картинок, это параметр заливки.
 *
 * Фон намеренно тёмный: патронус — светящееся существо, на белом листе
 * серебристое свечение попросту не видно.
 *
 * Форма зависит от уровня: у Роулинг патронус сначала бесформенный (щит из
 * серебристого дыма) и только окрепнув становится телесным, с фигурой зверя.
 */
function PatronAvatar({ patron, inventory = [], size = 240 }) {
  const color = patron?.color || '#e2e8f0';
  const level = Math.min(Math.max(patron?.level || 1, 1), 5);

  const worn = inventory.filter((i) => i.equipped);
  const head = worn.find((i) => i.slot === 'head');
  const neck = worn.find((i) => i.slot === 'neck');
  const held = worn.find((i) => i.slot === 'held');

  // Чем выше уровень, тем плотнее силуэт: дымка → сгусток → зверь.
  const bodyOpacity = [0.5, 0.62, 0.74, 0.86, 0.96][level - 1];
  const blur = [4.5, 3.4, 2.4, 1.6, 0.9][level - 1];
  const corporeal = level >= 4;

  // Звёзды на фоне — постоянные, чтобы не мигали при каждом рендере.
  const stars = [
    [26, 34, 1.1], [58, 20, 0.8], [168, 40, 1.2], [182, 96, 0.9],
    [22, 120, 0.9], [40, 172, 1.1], [150, 168, 0.8], [178, 148, 1.0],
    [96, 18, 0.7], [120, 182, 0.9],
  ];

  return (
    <svg viewBox="0 0 200 200" width={size} height={size} role="img" aria-label="Патронус">
      <defs>
        <radialGradient id="night" cx="50%" cy="45%" r="70%">
          <stop offset="0%" stopColor="#312e81" />
          <stop offset="60%" stopColor="#1e1b4b" />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>

        <radialGradient id="halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="55%" stopColor={color} stopOpacity="0.16" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>

        {/* мягкое размытие силуэта */}
        <filter id="soft" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={blur} />
        </filter>

        {/* настоящее свечение: размытая копия под оригиналом */}
        <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <clipPath id="scene">
          <rect x="0" y="0" width="200" height="200" rx="24" />
        </clipPath>
      </defs>

      <g clipPath="url(#scene)">
        <rect x="0" y="0" width="200" height="200" fill="url(#night)" />

        {stars.map(([x, y, r], i) => (
          <circle key={i} cx={x} cy={y} r={r} fill="#e0e7ff" opacity="0.5">
            <animate
              attributeName="opacity"
              values="0.2;0.7;0.2"
              dur={`${3 + (i % 4)}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* ореол вокруг патронуса */}
        <circle cx="100" cy="100" r="88" fill="url(#halo)">
          <animate attributeName="r" values="82;92;82" dur="4s" repeatCount="indefinite" />
        </circle>

        <g filter="url(#glow)">
          <g filter="url(#soft)" opacity={bodyOpacity}>
            {/* тело */}
            <ellipse cx="100" cy="120" rx="33" ry="42" fill={color} />
            {/* голова */}
            <circle cx="100" cy="74" r="25" fill={color} />

            {corporeal && (
              <>
                <path d="M83 55 L77 28 L95 48 Z" fill={color} />
                <path d="M117 55 L123 28 L105 48 Z" fill={color} />
                <path d="M130 132 Q158 122 148 94 Q166 126 136 150 Z" fill={color} opacity="0.9" />
              </>
            )}
          </g>

          {/* яркое ядро — источник света */}
          <ellipse cx="100" cy="104" rx="14" ry="20" fill={color} opacity={0.35 + level * 0.1}>
            <animate
              attributeName="opacity"
              values={`${0.3 + level * 0.08};${0.5 + level * 0.09};${0.3 + level * 0.08}`}
              dur="3.5s"
              repeatCount="indefinite"
            />
          </ellipse>
        </g>

        {/* глаза — у оформившегося патронуса */}
        {level >= 3 && (
          <g>
            <circle cx="92" cy="72" r="3" fill="#0f172a" opacity="0.8" />
            <circle cx="108" cy="72" r="3" fill="#0f172a" opacity="0.8" />
          </g>
        )}

        {/* Надетые вещи. Координаты подобраны по силуэту: голова — круг
            (100, 74) r25, тело — эллипс (100, 120). Иначе вещи висят в воздухе
            отдельно от патронуса. */}
        {neck && <text x="100" y="106" fontSize="24" textAnchor="middle">{neck.emoji}</text>}
        {head && <text x="100" y="58" fontSize="28" textAnchor="middle">{head.emoji}</text>}
        {held && <text x="130" y="134" fontSize="24" textAnchor="middle">{held.emoji}</text>}
      </g>
    </svg>
  );
}

export default PatronAvatar;
